import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  fetchWeather,
  fetchListWeather,
  WeatherItem,
  ListWeatherItem,
} from "api/Weather";
import { ClockInfo } from "./useClock";

export const iconUrl = (icon?: string) => (icon ? `https://openweathermap.org/img/wn/${icon}@2x.png` : "");

const hhmm = (unix?: number) =>
  unix ? new Date(unix * 1000).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }) : "--:--";

const dayLabel = (day: ListWeatherItem, index: number) =>
  index === 0 ? "Hôm nay" : new Date(day.dt * 1000).toLocaleDateString("vi-VN", { weekday: "short" });

/** Thời tiết hiện tại + dự báo 4 ngày (mốc 12:00) — dùng chung cho trang chủ và chân trang */
export function useWeather() {
  const [current, setCurrent] = useState<WeatherItem>();
  const [forecast, setForecast] = useState<ListWeatherItem[]>([]);
  useEffect(() => {
    fetchWeather().then(setCurrent).catch(() => undefined);
    fetchListWeather()
      .then((data) => setForecast(data.list.filter((item) => item.dt_txt.endsWith("12:00:00")).slice(0, 4)))
      .catch(() => undefined);
  }, []);
  return { current, forecast };
}
export type WeatherInfo = ReturnType<typeof useWeather>;

interface FooterProps {
  clock: ClockInfo;
  showTasks: boolean;
  weather: WeatherInfo;
}

export function Footer({ clock, showTasks, weather }: FooterProps) {
  const [weatherOpen, setWeatherOpen] = useState(false);
  const { time, seconds, dateLabel } = clock;

  const { current: weathers, forecast } = weather;

  const current = weathers?.weather[0];
  const temp = weathers ? `${Math.round(weathers.main.temp)}°` : "--°";

  return (
    <footer className="relative z-20 w-full shrink-0 flex items-end justify-between gap-4 pl-3 pr-20 sm:pl-4 lg:pl-6 lg:pr-24 pt-2 pb-3 lg:pb-5 xl:hidden">
      {/* Trái: Đồng hồ + nút nhanh */}
      <div className="flex flex-col gap-2 lg:gap-3 min-w-0">
        <div className="xl:hidden">
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl md:text-3xl lg:text-5xl font-light text-white tabular-nums">{time}</span>
            <span className="text-xs md:text-sm lg:text-xl font-light text-gray-400 tabular-nums">{seconds}</span>
          </div>
          <span className="hidden sm:block text-[10px] lg:text-xs text-gray-400 mt-1 uppercase tracking-wide truncate">{dateLabel}</span>
        </div>

        <div className="flex items-center gap-2">
          {showTasks && (
            <Link
              to="/work-report"
              aria-label="Công việc"
              title="Công việc"
              className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg transition"
            >
              <i className="fa-solid fa-check text-xs" />
            </Link>
          )}
        </div>
      </div>

      {/* Phải: Thời tiết — desktop: thẻ nằm ngang (hiện tại | dự báo 4 ngày) để không chiếm chiều cao */}
      <div className="hidden lg:flex xl:hidden items-stretch bg-gray-900/40 backdrop-blur-md border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="w-[230px] px-4 py-2.5 flex flex-col justify-center">
          <div className="flex items-center gap-2">
            {current && <img src={iconUrl(current.icon)} alt={current.description} className="w-11 h-11 -my-1 -ml-1" />}
            <div className="flex-1 min-w-0">
              <div className="text-2xl font-light text-white leading-none">{temp}</div>
              <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider truncate" title={current?.description}>
                {current?.description || "Đang tải thời tiết…"}
              </div>
            </div>
            <span className="self-start text-[9px] text-gray-400 bg-gray-800/60 px-1.5 py-0.5 rounded-full">TP.HCM</span>
          </div>
          <div className="flex items-center justify-between mt-2 text-[10px] text-gray-400">
            <span title="Gió"><i className="fa-solid fa-wind mr-1" />{weathers ? `${Math.round(weathers.wind.speed * 3.6)} km/h` : "--"}</span>
            <span title="Lượng mưa 1 giờ"><i className="fa-solid fa-droplet mr-1" />{weathers?.rain?.["1h"] ?? 0} mm</span>
            <span title="Bình minh"><i className="fa-solid fa-sun mr-1" />{hhmm(weathers?.sys.sunrise)}</span>
            <span title="Hoàng hôn"><i className="fa-regular fa-moon mr-1" />{hhmm(weathers?.sys.sunset)}</span>
          </div>
        </div>

        {forecast.length > 0 && (
          <div className="hidden xl:grid grid-cols-4 gap-0.5 px-1.5 py-1.5 border-l border-gray-700/50">
            {forecast.map((day, index) => (
              <div key={day.dt} className="w-12 flex flex-col items-center justify-center rounded-lg hover:bg-gray-700/40 transition">
                <span className="text-[9px] text-gray-400 uppercase font-medium whitespace-nowrap">{dayLabel(day, index)}</span>
                <img src={iconUrl(day.weather[0].icon)} alt={day.weather[0].description} className="w-8 h-8 -my-0.5" />
                <span className="text-[11px] font-semibold text-white">{Math.round(day.main.temp)}°</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phải: Thời tiết — tablet/mobile gọn, bấm để xem chi tiết */}
      <button
        type="button"
        onClick={() => setWeatherOpen(true)}
        className="lg:hidden flex items-center gap-1 text-xs text-gray-300 bg-gray-800/50 rounded-full pl-1 pr-3 py-0.5 hover:bg-gray-700/60 transition flex-shrink-0"
      >
        {current && <img src={iconUrl(current.icon)} alt={current.description} className="w-7 h-7" />}
        <span>{temp}</span>
      </button>

      {/* Weather Modal - Visible on smaller screens */}
      {weatherOpen && (
        <div className="fixed inset-0 lg:hidden z-50 bg-black/50 flex items-end" onClick={() => setWeatherOpen(false)}>
          <div className="w-full bg-gray-900/95 backdrop-blur-xl rounded-t-2xl p-4 max-h-[50vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-white">Dự báo thời tiết · TP.HCM</h3>
              <button type="button" onClick={() => setWeatherOpen(false)} className="text-gray-400 hover:text-white" aria-label="Đóng">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-3">
                {current && <img src={iconUrl(current.icon)} alt={current.description} />}
                <div>
                  <div className="text-2xl font-light text-white">{temp}C</div>
                  <div className="text-xs text-gray-400 uppercase">{current?.description}</div>
                  <div className="text-[11px] text-gray-400 mt-1">
                    <i className="fa-solid fa-sun mr-1" />{hhmm(weathers?.sys.sunrise)}
                    <i className="fa-regular fa-moon ml-3 mr-1" />{hhmm(weathers?.sys.sunset)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 pt-4 border-t border-gray-700">
                {forecast.map((day, index) => (
                  <div key={day.dt} className="flex flex-col items-center space-y-1 p-2 rounded-lg bg-gray-800/50">
                    <span className="text-[9px] text-gray-400 uppercase font-medium">{dayLabel(day, index)}</span>
                    <img src={iconUrl(day.weather[0].icon)} alt={day.weather[0].description} style={{ width: "24px" }} />
                    <span className="text-xs font-semibold text-white">{Math.round(day.main.temp)}°</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </footer>
  )
}